import React, { useEffect, useState } from 'react';
import { ClientSummary, PartnerTokenTier, insurerService } from '../../services/insurerService';
import { usePartnerTokenTiers } from '../../contexts/PartnerTokenTiersContext';

export const PARTNER_TOKEN_OPTIONS: Array<{
  tier: PartnerTokenTier;
  title: string;
  description: string;
}> = [
  {
    tier: 'simple',
    title: 'Prepared',
    description:
      'Create your own virtual tabletop scenario and complete on your own time.',
  },
  {
    tier: 'gold',
    title: 'Prepared+',
    description:
      'Create your own virtual tabletop scenario and submit your results for an after-action ISO 22301 compliance assessment. When the client uses the token, Flash is notified to begin the assessment.',
  },
  {
    tier: 'premium',
    title: 'Executive',
    description:
      'Create your own virtual tabletop scenario and schedule professional facilitation by a Flash AI facilitator. When the client uses the token, Flash AI is notified to coordinate facilitation. Facilitation is not an ISO certification.',
  },
];

interface GrantTokenModalProps {
  isOpen: boolean;
  client: ClientSummary | null;
  onClose: () => void;
  onGranted: (message: string) => void;
  onError: (message: string) => void;
}

const GrantTokenModal: React.FC<GrantTokenModalProps> = ({
  isOpen,
  client,
  onClose,
  onGranted,
  onError,
}) => {
  const { tokenOptions, refresh } = usePartnerTokenTiers();
  const optionsToDisplay = tokenOptions.length > 0 ? (tokenOptions as typeof PARTNER_TOKEN_OPTIONS) : PARTNER_TOKEN_OPTIONS;
  const [selectedTier, setSelectedTier] = useState<PartnerTokenTier>('simple');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      refresh();
      setSelectedTier('simple');
      setSubmitting(false);
    }
  }, [isOpen, client?.id, refresh]);

  if (!isOpen || !client) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    try {
      const result = await insurerService.grantClientCredit(client.id, selectedTier);
      onGranted(result.message);
      onClose();
    } catch (err: any) {
      onError(err?.response?.data?.detail || err?.message || 'Failed to grant token');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedOption = optionsToDisplay.find((option) => option.tier === selectedTier);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        <div className="fixed inset-0 bg-black/40" onClick={submitting ? undefined : onClose} aria-hidden />

        <form
          onSubmit={handleSubmit}
          className="relative w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl"
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Grant tabletop token</h3>
              <p className="mt-1 text-sm text-gray-600">
                Choose a token for <span className="font-medium text-gray-900">{client.email}</span>.
                This will be added to your billing ledger.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
              aria-label="Close"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-3">
            {optionsToDisplay.map((option) => {
              const selected = selectedTier === option.tier;
              return (
                <label
                  key={option.tier}
                  className={`block cursor-pointer rounded-lg border p-4 transition ${
                    selected
                      ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="token-tier"
                      value={option.tier}
                      checked={selected}
                      onChange={() => setSelectedTier(option.tier)}
                      className="mt-1"
                    />
                    <div className="min-w-0 flex-1">
                      <span className="font-semibold text-gray-900">{option.title}</span>
                      <p className="mt-1 text-sm text-gray-600">{option.description}</p>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          {selectedOption && (
            <p className="mt-4 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
              You are granting a <strong>{selectedOption.title}</strong> to {client.email}.
            </p>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Granting…' : `Grant ${selectedOption?.title ?? 'token'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GrantTokenModal;
